import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useChatStore } from '../../stores/chatStore'
import { fetchMessages, mapApiMessageToStore } from '../../lib/chatApi'

type ConnectionStatus =
  | 'disconnected' | 'connecting' | 'provisioning' | 'sandbox_ready' | 'thinking' | 'connected'

interface ServerEvent {
  type:
    | 'text' | 'thinking' | 'tool_call' | 'tool_result' | 'error' | 'done' | 'status'
    | 'title_updated' | 'result_saved' | 'chart_saved' | 'spreadsheet_saved'
    | 'chart_failed' | 'result_failed' | 'spreadsheet_failed'
    | 'resume_turn' | 'user_message' | 'data_sources_changed'
  content?: string
  phase?: 'commentary' | 'final_answer'
  status?: string
  tool_name?: string
  tool_input?: Record<string, unknown>
  tool_use_id?: string
  is_error?: boolean
  conversation_id?: string
  title?: string
  interrupted?: boolean
  empty?: boolean
  result_id?: string
  chart_id?: string
  spreadsheet_id?: string
  attachments?: Array<{ mime_type: string; data_base64: string }>
  artifact_id?: string
  version_num?: number
}

export interface UseAgentOptions {
  conversationId?: string | null
  workspaceKey?: string | null
}

// Ported from OLD hooks/useAgent.ts (analyst-builder + sharing branches
// removed). The WS state machine, exponential-backoff reconnect, 4001→login,
// and resume_turn buffering are preserved verbatim.
export function useAgent(opts: UseAgentOptions = {}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reconnectDelay = useRef(1000)
  const convIdRef = useRef<string | null>(null)
  const connectRef = useRef<(conversationId: string | null) => void>(() => {})

  const connectionStatus = useChatStore((s) => s.connectionStatus)
  const isStreaming = useChatStore((s) => s.isStreaming)
  const setConnectionStatus = useChatStore((s) => s.setConnectionStatus)
  const setIsStreaming = useChatStore((s) => s.setIsStreaming)

  const workspaceKey = opts.workspaceKey ?? ''
  const targetConvId = workspaceKey ? opts.conversationId ?? null : null
  const storeActions = useChatStore.getState

  useEffect(() => {
    convIdRef.current = targetConvId
  }, [targetConvId])

  const streamingMsgRef = useRef(false)
  const stoppedRef = useRef(false)
  const resumingRef = useRef(false)
  const resumeBufferRef = useRef<ServerEvent[]>([])

  const connect = useCallback((conversationId: string | null) => {
    const {
      addMessage,
      appendTextToLastAi,
      addThinkingToLastAi,
      addToolCallToLastAi,
      addToolResultToLastAi,
      markLastAiInterrupted,
      removeLastAiIfEmpty,
      setMessages,
      updateConversationTitle,
      recordFailedArtifact,
    } = storeActions()

    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }

    streamingMsgRef.current = false
    setIsStreaming(false)

    if (conversationId == null) {
      setConnectionStatus('disconnected')
      return
    }

    setConnectionStatus('connecting')

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws`)
    wsRef.current = ws

    ws.onopen = () => {
      reconnectDelay.current = 1000
      setConnectionStatus('connecting')
      ws.send(JSON.stringify({ type: 'init', conversation_id: conversationId, workspace_key: workspaceKey }))
    }

    const applyEvent = (data: ServerEvent) => {
      switch (data.type) {
        case 'title_updated':
          if (data.conversation_id != null && data.title) {
            updateConversationTitle(data.conversation_id, data.title)
          }
          break

        case 'user_message':
          if (
            convIdRef.current &&
            (data.content != null || (data.attachments && data.attachments.length > 0))
          ) {
            addMessage(convIdRef.current, {
              role: 'user',
              content: data.content ?? '',
              attachments: data.attachments?.map((a) => ({
                mimeType: a.mime_type,
                dataBase64: a.data_base64,
              })),
            })
          }
          break

        case 'status':
          setConnectionStatus(data.status as ConnectionStatus)
          if (data.status === 'sandbox_ready' && convIdRef.current) {
            queryClient.invalidateQueries({ queryKey: ['messages', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['conversationResults', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['conversationCharts', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['conversationSpreadsheets', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['allCharts', workspaceKey] })
            queryClient.invalidateQueries({ queryKey: ['allResults', workspaceKey] })
            queryClient.invalidateQueries({ queryKey: ['allSpreadsheets', workspaceKey] })
            // Box is up — flush the first message of a freshly-created chat.
            const pf = storeActions().pendingFirstMessage
            if (pf && pf.conversationId === convIdRef.current && ws.readyState === WebSocket.OPEN) {
              storeActions().setPendingFirstMessage(null)
              storeActions().addMessage(convIdRef.current, {
                role: 'user',
                content: pf.text,
                attachments: pf.attachments?.map((a) => ({
                  mimeType: a.mime_type,
                  dataBase64: a.data_base64,
                })),
              })
              const frame: Record<string, unknown> = { type: 'message', content: pf.text }
              if (pf.attachments && pf.attachments.length > 0) frame.attachments = pf.attachments
              ws.send(JSON.stringify(frame))
            }
          }
          if (data.status === 'thinking' && convIdRef.current) {
            stoppedRef.current = false
            addMessage(convIdRef.current, { role: 'ai', content: '', blocks: [] })
            streamingMsgRef.current = true
            setIsStreaming(true)
          }
          break

        case 'text':
          if (data.content && convIdRef.current) {
            if (!streamingMsgRef.current) {
              if (stoppedRef.current) break
              addMessage(convIdRef.current, { role: 'ai', content: '', blocks: [] })
              streamingMsgRef.current = true
              setIsStreaming(true)
            }
            appendTextToLastAi(convIdRef.current, data.content, data.phase)
          }
          break

        case 'thinking':
          if (data.content && streamingMsgRef.current && convIdRef.current) {
            addThinkingToLastAi(convIdRef.current, data.content)
          }
          break

        case 'tool_call':
          if (streamingMsgRef.current && data.tool_name && convIdRef.current) {
            addToolCallToLastAi(convIdRef.current, data.tool_name, data.tool_input ?? null)
          }
          break

        case 'tool_result':
          if (streamingMsgRef.current && data.content && convIdRef.current) {
            addToolResultToLastAi(convIdRef.current, data.content, data.is_error ?? false)
          }
          break

        case 'result_saved':
          if (convIdRef.current) {
            queryClient.invalidateQueries({ queryKey: ['conversationResults', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['allResults', workspaceKey] })
          }
          break

        case 'chart_saved':
          if (convIdRef.current) {
            queryClient.invalidateQueries({ queryKey: ['conversationCharts', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['allCharts', workspaceKey] })
          }
          break

        case 'spreadsheet_saved':
          if (convIdRef.current) {
            queryClient.invalidateQueries({ queryKey: ['conversationSpreadsheets', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['allSpreadsheets', workspaceKey] })
            // Auto-open the editor side panel on the new version (OLD behaviour).
            if (data.artifact_id && data.version_num != null) {
              const artifactId = data.artifact_id
              const versionNum = data.version_num
              void navigate({
                to: '/assistant/$conversationId',
                params: { conversationId: convIdRef.current },
                search: (prev) => ({ ...prev, sheet: artifactId, v: versionNum }),
              })
            }
          }
          break

        case 'result_failed':
          if (data.result_id) recordFailedArtifact('result', data.result_id)
          break

        case 'chart_failed':
          if (data.chart_id) recordFailedArtifact('chart', data.chart_id)
          break

        case 'spreadsheet_failed':
          if (data.spreadsheet_id) recordFailedArtifact('spreadsheet', data.spreadsheet_id)
          break

        case 'data_sources_changed':
          if (convIdRef.current) {
            queryClient.invalidateQueries({ queryKey: ['conversationDataSources', workspaceKey, convIdRef.current] })
          }
          break

        case 'error':
          if (data.content && convIdRef.current) {
            if (!streamingMsgRef.current) {
              if (stoppedRef.current) break
              addMessage(convIdRef.current, {
                role: 'ai',
                content: `**Error:** ${data.content}`,
                blocks: [{ type: 'text', text: `**Error:** ${data.content}` }],
              })
            } else {
              appendTextToLastAi(convIdRef.current, `\n\n**Error:** ${data.content}`)
            }
          }
          streamingMsgRef.current = false
          setIsStreaming(false)
          break

        case 'done':
          streamingMsgRef.current = false
          setIsStreaming(false)
          setConnectionStatus('connected')
          if (data.empty && convIdRef.current) {
            removeLastAiIfEmpty(convIdRef.current)
          } else if (convIdRef.current) {
            if (data.interrupted) {
              markLastAiInterrupted(convIdRef.current)
            } else {
              queryClient.invalidateQueries({ queryKey: ['messages', workspaceKey, convIdRef.current] })
            }
            queryClient.invalidateQueries({ queryKey: ['conversationResults', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['conversationCharts', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['conversationSpreadsheets', workspaceKey, convIdRef.current] })
            queryClient.invalidateQueries({ queryKey: ['allCharts', workspaceKey] })
            queryClient.invalidateQueries({ queryKey: ['allResults', workspaceKey] })
            queryClient.invalidateQueries({ queryKey: ['allSpreadsheets', workspaceKey] })
          }
          stoppedRef.current = false
          break
      }
    }

    const handleResumeTurn = async () => {
      const convId = convIdRef.current
      resumingRef.current = true
      try {
        if (convId != null) {
          const fresh = await fetchMessages(workspaceKey, convId)
          if (convIdRef.current === convId) {
            setMessages(convId, fresh.map(mapApiMessageToStore))
          }
        }
      } catch {
        void 0
      }
      stoppedRef.current = false
      streamingMsgRef.current = true
      setIsStreaming(true)
      setConnectionStatus('thinking')
      resumingRef.current = false
      const buffered = resumeBufferRef.current
      resumeBufferRef.current = []
      for (const ev of buffered) applyEvent(ev)
    }

    ws.onmessage = (event) => {
      let data: ServerEvent
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }
      if (data.type === 'resume_turn') {
        void handleResumeTurn()
        return
      }
      if (resumingRef.current) {
        resumeBufferRef.current.push(data)
        return
      }
      applyEvent(data)
    }

    ws.onclose = (event) => {
      setConnectionStatus('disconnected')
      setIsStreaming(false)
      streamingMsgRef.current = false
      if (event.code === 4001) {
        window.location.href = '/auth/login'
        return
      }
      const currentConvId = convIdRef.current
      if (currentConvId === conversationId) {
        reconnectTimer.current = setTimeout(() => connectRef.current(conversationId), reconnectDelay.current)
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 15000)
      }
    }

    ws.onerror = () => {
      ws.close()
    }
  }, [storeActions, queryClient, navigate, setConnectionStatus, setIsStreaming, workspaceKey])

  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  useEffect(() => {
    clearTimeout(reconnectTimer.current)
    streamingMsgRef.current = false
    connect(targetConvId)
    return () => {
      clearTimeout(reconnectTimer.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [targetConvId, connect])

  const sendMessage = useCallback(
    (
      content: string,
      attachments?: Array<{ mime_type: string; data_base64: string }>,
    ) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
      if (isStreaming) return
      if (!convIdRef.current) return
      storeActions().addMessage(convIdRef.current, {
        role: 'user',
        content,
        attachments: attachments?.map((a) => ({
          mimeType: a.mime_type,
          dataBase64: a.data_base64,
        })),
      })
      const frame: Record<string, unknown> = { type: 'message', content }
      if (attachments && attachments.length > 0) frame.attachments = attachments
      wsRef.current.send(JSON.stringify(frame))
    },
    [isStreaming, storeActions],
  )

  const stopGeneration = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    if (!isStreaming) return
    stoppedRef.current = true
    wsRef.current.send(JSON.stringify({ type: 'stop' }))
  }, [isStreaming])

  return {
    sendMessage,
    stopGeneration,
    connectionStatus,
    isStreaming,
    isConnected: connectionStatus !== 'disconnected',
  }
}
